import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { admin, login } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (admin) navigate("/admin");
  }, [admin, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-5" data-testid="admin-login">
      <div className="w-full max-w-sm">
        <a href="/" className="font-serif text-3xl text-ink block mb-2">Chateaux</a>
        <p className="text-xs uppercase tracking-[0.2em] text-coral mb-8">Content Studio</p>

        <form onSubmit={submit} className="border border-ink/15 p-8 bg-bone">
          <div className="w-10 h-10 flex items-center justify-center bg-ink text-bone mb-6">
            <Lock className="w-5 h-5" />
          </div>
          <h1 className="font-serif text-3xl text-ink mb-6">Sign in</h1>

          <label className="block text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-email"
            className="w-full bg-transparent border border-ink/20 px-4 py-3 text-ink focus:border-coral focus:outline-none mb-5"
          />
          <label className="block text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">Password</label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password"
            className="w-full bg-transparent border border-ink/20 px-4 py-3 text-ink focus:border-coral focus:outline-none mb-6"
          />

          {error && (
            <p className="text-sm text-coral mb-4" data-testid="admin-login-error">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="group w-full inline-flex items-center justify-center gap-2 bg-coral text-bone py-3.5 text-sm font-medium hover:bg-ink transition-colors disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
            {!loading && <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />}
          </button>
        </form>
      </div>
    </div>
  );
}
